import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MainWebsitesColumns, MainWebsiteTableDataType } from "./types";
import { getCounter } from "../../lib/functions";
import { Button, Drawer, Image, Input, message, Table } from "antd";
import { deleteMainWebsite, getMainWebsites } from "./actions";
import SectionTitle from "../../Components/SectionTitle";
import { PiPlus } from "react-icons/pi";
import { CLOUD_URL } from "../../lib/env";
import DashPagination from "../../Components/DashPagination";
import DashModal from "../../Components/DashModal";
import AddNewMainWebsite from "./Components/AddNewMainWebsite";
import EditMainWebsiteTitle from "./Components/EditMainWebsite";
import Top10MainWebsitesKeywordManager from "./Components/MainWebsiteKeywordsManager";
import MainWebsitesGeneralEdit from "./Components/MainWebsiteGeneralEdit";
import MainWebsitesEditDescription from "./Components/EditDescription";
import EditMainWebsitesImage from "./Components/EditMainWebsiteImage";
import Categories from "./Components/Categories";
import Homepage from "./Components/Homepage";

const { Search } = Input;

type Props = {
  addNewMainWebsite?: boolean;
  editMainWebsiteTitle?: boolean;
  editMainWebsiteDescription?: boolean;
  editKeywords?: boolean;
  editImage?: boolean;
  manageCategories?: boolean;
  edit?: boolean;
  addNewCategory?: boolean;
  editCategoryImage?: boolean;
  editCategoryTitle?: boolean;
  editCategoryKeywords?: boolean;
  editCategory?: boolean;
  editCategoryDescription?: boolean;
  editHomepage?: boolean;
  editCategoryTop10?: boolean;
  manageCategoryRichText?: boolean;
  addNewRichText?: boolean;
  editRichText?: boolean;
  sortItems?: boolean;
};

const MainWebsites = (props: Props) => {
  const router = useNavigate();

  const [data, setData] = useState<MainWebsiteTableDataType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (
    expanded: boolean,
    record: MainWebsiteTableDataType
  ) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("MainWebsites");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const websites = await getMainWebsites(pageSize, page, query);
      const formatted = websites.map((wb) => ({
        id: wb.id,
        title: wb.MainInfo.Title,
        image: wb.MainInfo.Image,
        keywords: wb.MainInfo.Keywords,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch websites");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteMainWebsite(id);
      message.success("Article deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete website");
    }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Main Websites" />
      <div className="flex justify-between items-center">
        <Link to={`/main-websites/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PiPlus />}>
            Add New Main Website
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search website..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>
      <Table<MainWebsiteTableDataType>
        rowKey="id"
        columns={MainWebsitesColumns(page, pageSize, handleDelete)}
        dataSource={data}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <Image width={200} src={CLOUD_URL + "/" + record.image} />
          ),
          rowExpandable: () => true,
          onExpand: handleExpand,
          expandedRowKeys,
        }}
      />{" "}
      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      {props.addNewMainWebsite && (
        <DashModal
          title="Add New Main Website"
          width={800}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewMainWebsite fetchData={fetchData} />
        </DashModal>
      )}
      {props.editMainWebsiteTitle && (
        <DashModal
          title="Edit Main Website"
          width={1200}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditMainWebsiteTitle fetchData={fetchData} />
        </DashModal>
      )}
      {props.editKeywords && (
        <DashModal
          title="Edit Main Websites"
          width={1200}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Top10MainWebsitesKeywordManager fetchData={fetchData} />
        </DashModal>
      )}
      {props.editMainWebsiteDescription && (
        <DashModal
          title="Edit Main Websites"
          width={1200}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <MainWebsitesEditDescription fetchData={fetchData} />
        </DashModal>
      )}
      {props.editImage && (
        <DashModal
          title="Edit Main Websites Image"
          width={600}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditMainWebsitesImage fetchData={fetchData} />
        </DashModal>
      )}
      {props.editHomepage && (
        <DashModal
          title="Edit Main Websites Homepage"
          width={1200}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Homepage />
        </DashModal>
      )}
      {props.manageCategories && (
        <DashModal
          title="Manage Categories"
          width={5000}
          cancleLink={`/main-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Categories
            editCategory={props.editCategory}
            editKeywords={props.editCategoryKeywords}
            editTitle={props.editCategoryTitle}
            editImage={props.editCategoryImage}
            addNewCategory={props.addNewCategory}
            editDescription={props.editCategoryDescription}
            editTop10={props.editCategoryTop10}
            manageRichText={props.manageCategoryRichText}
            addNewRichText={props.addNewRichText}
            editRichText={props.editRichText}
            sortItems={props.sortItems}
            page={page}
            pageSize={pageSize}
          />
        </DashModal>
      )}
      {props.edit && (
        <Drawer
          title="Edit Category"
          onClose={() =>
            router(`/main-websites?page=${page + 1}&pageSize=${pageSize}`)
          }
          open={true}
        >
          <MainWebsitesGeneralEdit page={page} pageSize={pageSize} />
        </Drawer>
      )}
    </div>
  );
};

export default MainWebsites;
